import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, MessageSquarePlus, RefreshCw, RotateCcw } from "lucide-react";
import { captureException } from "@/lib/sentry";
import { errorReportContext, type ErrorReportContext } from "@/lib/errorReport";
import { ErrorReportDialog } from "@/components/ErrorReportDialog";

interface Props {
  children: ReactNode;
  /**
   * When this changes while the fallback is showing, the error is cleared and
   * the children render again. Pass the current pathname so a crash on one
   * page does not follow the user to the next one — without it a caught error
   * is sticky for the life of the boundary, and a boundary around a portal's
   * `<Routes>` would blank every page after the first bad one.
   */
  resetKey?: string;
  /**
   * `page` (default) — full-height fallback with reload only; for the app
   * root, where nothing else is left to show.
   * `inline` — sized to sit inside a shell that stays mounted; offers
   * "Try again" (re-render the children in place) as well as reload.
   *
   * Both variants offer "Report an issue": a crash screen that only
   * reloads is a dead end, and the reference it shows is the one thing that
   * lets a report be matched to the event.
   */
  variant?: "page" | "inline";
}

interface State {
  hasError: boolean;
  /** Built once per caught error, so the reference in the dialog cannot drift. */
  context: ErrorReportContext | null;
  reportOpen: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, context: null, reportOpen: false };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const context = errorReportContext(
      this.props.variant === "inline" ? "inline" : "page",
      error,
    );
    this.setState({ context });
    void captureException(error, {
      tags: {
        reference: context.reference,
        route: context.route,
        surface: context.surface,
      },
      extra: {
        componentStack: info.componentStack,
        url: typeof window !== "undefined" ? window.location.href : undefined,
      },
    });
    console.error("[ErrorBoundary]", error.message);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, context: null, reportOpen: false });
    }
  }

  private reset = () => {
    this.setState({ hasError: false, context: null, reportOpen: false });
  };

  private setReportOpen = (reportOpen: boolean) => {
    this.setState({ reportOpen });
  };

  /** Reference line + report action, shared by both fallbacks. */
  private renderReportRow() {
    const { context, reportOpen } = this.state;

    return (
      <>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {this.props.variant === "inline" && (
            <button
              type="button"
              onClick={this.reset}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Try again
            </button>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={
              this.props.variant === "inline"
                ? "flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                : "flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            }
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {this.props.variant === "inline" ? "Reload" : "Refresh page"}
          </button>
          <button
            type="button"
            onClick={() => this.setReportOpen(true)}
            disabled={!context}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
          >
            <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
            Report an issue
          </button>
        </div>
        {context && (
          <p className="text-xs text-muted-foreground">
            Reference <span className="font-mono select-all">{context.reference}</span>
          </p>
        )}
        {context && (
          <ErrorReportDialog
            open={reportOpen}
            onOpenChange={this.setReportOpen}
            context={context}
          />
        )}
      </>
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.variant === "inline") {
      return (
        <div
          role="alert"
          data-testid="error-boundary-inline"
          className="flex flex-col items-center justify-center gap-4 rounded-lg border border-border bg-card px-6 py-12 text-center text-card-foreground"
        >
          <AlertTriangle className="h-10 w-10 text-warning-text" aria-hidden="true" />
          <div>
            <h2 className="text-lg font-semibold">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              This page hit an error. Try again, or reload if it keeps happening.
            </p>
          </div>
          {this.renderReportRow()}
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-dvh gap-6 bg-background text-foreground px-6">
        {/* PR #376: decorative icon must be hidden from assistive tech --
            screen readers announced it as "warning" alongside the heading,
            duplicating the message. Also adds the missing type="button"
            (suppressed observation S06) so this fallback can never submit a
            surrounding form. */}
        <AlertTriangle className="h-12 w-12 text-warning-text" aria-hidden="true" />
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground max-w-md">
            We apologize for the inconvenience. Please refresh the page to continue.
          </p>
        </div>
        {this.renderReportRow()}
      </div>
    );
  }
}
